// reCAPTCHA Configuration and Utilities
export const RECAPTCHA_SITE_KEY = '6LdetIorAAAAAI1BbkZFcGMfnRaaJQ6aWn3PQWa1'
export const RECAPTCHA_SECRET_KEY = '6LdetIorAAAAAKCk-ofUTtUiWRm6VpTDxPpMjQss'

// Types
export interface RecaptchaResponse {
  success: boolean
  challenge_ts?: string
  hostname?: string
  'error-codes'?: string[]
}

export interface UseRecaptchaOptions {
  onSuccess?: (token: string) => void
  onError?: (error: string) => void
}

export interface RecaptchaModalProps {
  isOpen: boolean
  onClose: () => void
  onVerify: (token: string) => void
  onError: (error: string) => void
  title?: string
}

// Add reCAPTCHA types to window object
declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void
      render: (container: string | HTMLElement, options: any) => number
      getResponse: (widgetId: number) => string
      reset: (widgetId: number) => void
    }
  }
}

// Load reCAPTCHA script
export const loadRecaptchaScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Check if script is already loaded
    if (typeof window !== 'undefined' && window.grecaptcha) {
      resolve()
      return
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector('script[src*="recaptcha/api.js"]')
    if (existingScript) {
      // Wait for existing script to load
      existingScript.addEventListener('load', () => resolve())
      existingScript.addEventListener('error', () => reject(new Error('Failed to load reCAPTCHA script')))
      return
    }

    const script = document.createElement('script')
    script.src = 'https://www.google.com/recaptcha/api.js'
    script.async = true
    script.defer = true
    
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load reCAPTCHA script'))
    
    document.head.appendChild(script)
  })
}

// Render reCAPTCHA widget
export const renderRecaptcha = (container: string | HTMLElement, options: any = {}): Promise<number> => {
  if (typeof window === 'undefined') {
    throw new Error('reCAPTCHA can only be executed in browser')
  }

  return new Promise((resolve, reject) => {
    loadRecaptchaScript()
      .then(() => {
        if (!window.grecaptcha) {
          reject(new Error('reCAPTCHA not loaded'))
          return
        }

        window.grecaptcha.ready(() => {
          try {
            // Check if container already has a reCAPTCHA widget
            const containerElement = typeof container === 'string' 
              ? document.querySelector(container) 
              : container
            
            if (containerElement) {
              const existingWidget = containerElement.querySelector('.g-recaptcha')
              if (existingWidget) {
                // Try to get existing widget ID
                const widgetId = parseInt(existingWidget.getAttribute('data-widget-id') || '0')
                if (widgetId > 0) {
                  resolve(widgetId)
                  return
                }
              }
            }

            const widgetId = window.grecaptcha.render(container, {
              sitekey: RECAPTCHA_SITE_KEY,
              theme: 'light',
              size: 'normal',
              ...options
            })
            resolve(widgetId)
          } catch (error) {
            reject(error)
          }
        })
      })
      .catch(reject)
  })
}

// Get reCAPTCHA response
export const getRecaptchaResponse = (widgetId: number): string => {
  if (typeof window === 'undefined' || !window.grecaptcha) {
    return ''
  }
  return window.grecaptcha.getResponse(widgetId)
}

// Reset reCAPTCHA widget
export const resetRecaptcha = (widgetId: number): void => {
  if (typeof window === 'undefined' || !window.grecaptcha) {
    return
  }
  window.grecaptcha.reset(widgetId)
}

// Verify reCAPTCHA token on server side
export const verifyRecaptchaToken = async (token: string): Promise<boolean> => {
  try {
    const response = await fetch('/api/verify-recaptcha', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token }),
    })

    if (!response.ok) {
      return false
    }

    const result = await response.json()
    return result.success
  } catch (error) {
    console.error('reCAPTCHA verification error:', error)
    return false
  }
} 